<template>
    <div class="user-info" :class="{ 'user-info--organization': showOrganization }" @click.prevent="handleClick">
        <Avatar v-if="showAvatar" :user="user" :selectable="true" :is-selected="isSelected" class="user-info__avatar" />
        <OrganizationLogo v-if="showOrganization" :user="user" class="user-info__logo" />
        <div v-if="customDetails" class="user-info__details">
            {{ customDetails }}
        </div>
        <div v-else class="user-info__details">
            <div class="user-info__name">
                {{ displayName }}
            </div>

            <div v-if="showMeta" class="user-info__meta">
                <span v-if="user.deletedAt !== null" class="deleted-notice">
                    {{ $t('user_info.account_deleted_info') }}
                </span>
                <template v-else-if="user.lastLoginAt === null">
                    {{ $t('user_info.never_logged_in') }}
                </template>
                <template v-else>
                    {{ formattedLastLogin }}
                </template>
            </div>
            <a v-if="showRatings" class="user-info__ratings-link" @click="$emit('ratings-click')">
                <Ratings
                    v-if="$store.getters.isCountrySweden || reviewCount > 0"
                    :value="averageRating"
                    :number-of-votes="reviewCount"
                    class="user-info__ratings"
                />
            </a>
        </div>
    </div>
</template>

<script>
import parseISO from 'date-fns/parseISO';
import differenceInDays from 'date-fns/differenceInDays';
import format from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import Ratings from '~/components/Ratings.vue';
import OrganizationLogo from '~/components/OrganizationLogo.vue';
import Avatar from '~/components/Avatar.vue';

export default {
    components: {
        Avatar,
        OrganizationLogo,
        Ratings,
    },
    props: {
        user: {
            type: Object,
            default: () => {},
        },
        forceShowUser: {
            type: Boolean,
            default: false,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        showMeta: {
            type: Boolean,
            default: true,
        },
        showRatings: {
            type: Boolean,
            default: false,
        },
        averageRating: {
            type: Number,
            default: 0,
        },
        reviewCount: {
            type: Number,
            default: 0,
        },
        customDetails: {
            type: String,
            default: null,
        },
    },
    computed: {
        showAvatar() {
            return this.forceShowUser || !this.user.isOrganizationRepresentative;
        },
        showOrganization() {
            return !this.forceShowUser && this.user.isOrganizationRepresentative;
        },
        displayName() {
            if (!this.user.isOrganizationRepresentative || !this.organization.name) {
                return this.user.name;
            }

            if (this.forceShowUser) {
                return `${this.user.firstName} på ${this.organization.name}`;
            }

            return this.organization.name;
        },
        formattedLastLogin() {
            const userLastLogin = parseISO(this.user.lastLoginAt);
            const daysSinceLastLogin = differenceInDays(Date.now(), userLastLogin);

            if (daysSinceLastLogin) {
                const lastLogin = format(userLastLogin, this.$utils.defaultDateFormat(), {
                    locale: this.$utils.getDateFnsLocale(),
                });

                return this.$t('user_info.last_login_date', { lastLogin });
            }

            const distance = formatDistanceToNow(userLastLogin, {
                locale: this.$utils.getDateFnsLocale(),
            });

            return this.$t('user_info.last_login_relative', { distance });
        },
        organization() {
            if (!this.user.isOrganizationRepresentative) {
                return {};
            }

            return {
                name: this.user.organizationName,
                logoUrl: this.user.organizationLogotypeUrl,
            };
        },
    },
    methods: {
        handleClick() {
            this.$emit('click');
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.user-info {
    display: flex;
    align-items: center;

    &--organization {
        flex-direction: column;
    }

    &__avatar {
        .avatar {
            width: 60px;
            min-width: 60px;
            height: 60px;
            margin-right: 10px;
        }
    }

    &__logo {
        width: 240px;
        max-height: 150px;
        margin-bottom: 10px;

        img {
            max-height: 150px;
        }
    }

    &__details {
        display: flex;
        flex-direction: column;
        text-align: left;
        line-height: 1.3em;
    }

    &__name {
        margin-left: 5px; // Accounts for a margin that the star icon in Ratings has
    }

    &__meta {
        color: $secondary-text-color;
        @include font-size(14px);
        margin-left: 5px; // Accounts for a margin that the star icon in Ratings has
        margin-top: 10px;

        .deleted-notice {
            font-style: italic;
        }
    }

    &__ratings {
        margin: 4px 0;
    }

    &__ratings-link {
        text-decoration: none;
    }
}
</style>
